import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePlantillas } from '../hooks/usePlantillas';
import type { ModoImportacionPlantilla } from '../types';

interface ModalImportarPlantillaProps {
  isOpen: boolean;
  onClose: () => void;
  /** Si el presupuesto ya tiene ítems hay que elegir agregar o reemplazar. */
  tieneItems: boolean;
  onImportar: (plantillaId: string, modo?: ModoImportacionPlantilla) => Promise<unknown>;
  isImportando?: boolean;
}

const OPCIONES_MODO: { value: ModoImportacionPlantilla; label: string; descripcion: string }[] = [
  {
    value: 'agregar',
    label: 'Agregar a los ítems actuales',
    descripcion: 'Los ítems de la plantilla se suman al final de cada caja.',
  },
  {
    value: 'reemplazar',
    label: 'Reemplazar los ítems actuales',
    descripcion: 'Se eliminan los ítems actuales y se cargan los de la plantilla.',
  },
];

/** Importa los ítems activos de una plantilla en un presupuesto independiente. */
export const ModalImportarPlantilla: React.FC<ModalImportarPlantillaProps> = ({
  isOpen,
  onClose,
  tieneItems,
  onImportar,
  isImportando = false,
}) => {
  const { plantillas, isLoading } = usePlantillas({ soloActivas: true, enabled: isOpen });
  const [plantillaId, setPlantillaId] = useState('');
  const [modo, setModo] = useState<ModoImportacionPlantilla>('agregar');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setPlantillaId('');
    setModo('agregar');
    setError('');
  }, [isOpen]);

  const handleImportar = async () => {
    if (!plantillaId) {
      setError('Seleccioná una plantilla');
      return;
    }

    try {
      await onImportar(plantillaId, tieneItems ? modo : undefined);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'No se pudo importar la plantilla.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(abierto) => !abierto && !isImportando && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar plantilla</DialogTitle>
          <DialogDescription>
            Se copian los ítems activos de la plantilla. Los que se calculan sobre la base
            imponible toman la del presupuesto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="plantilla-importar">Plantilla</Label>
            <Select
              value={plantillaId}
              onValueChange={(valor) => {
                setPlantillaId(valor);
                setError('');
              }}
              disabled={isLoading || isImportando}
            >
              <SelectTrigger id="plantilla-importar" aria-label="Plantilla a importar">
                <SelectValue placeholder={isLoading ? 'Cargando…' : 'Seleccioná una plantilla'} />
              </SelectTrigger>
              <SelectContent>
                {plantillas.map((plantilla) => (
                  <SelectItem key={plantilla.id} value={plantilla.id}>
                    {plantilla.nombre} ({plantilla.cantidadItems} ítems)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoading && plantillas.length === 0 && (
              <p className="text-sm text-muted-foreground">No hay plantillas activas.</p>
            )}
          </div>

          {tieneItems && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                El presupuesto ya tiene ítems. ¿Qué querés hacer?
              </legend>
              {OPCIONES_MODO.map((opcion) => (
                <label
                  key={opcion.value}
                  className="flex cursor-pointer items-start gap-2 rounded-lg border p-3"
                >
                  <input
                    type="radio"
                    name="modo-importacion"
                    value={opcion.value}
                    checked={modo === opcion.value}
                    onChange={() => setModo(opcion.value)}
                    disabled={isImportando}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium">{opcion.label}</span>
                    <span className="block text-sm text-muted-foreground">{opcion.descripcion}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isImportando}>
            Cancelar
          </Button>
          <Button onClick={() => void handleImportar()} disabled={isImportando || !plantillaId}>
            Importar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
